import { History } from 'history';
import { t } from 'i18next';

import avantCitiesRoutes from 'avantCitiesRoutes';
import { RECORD_TYPE_OPTIONS } from 'constants/leaseType';
import { BUILDING_DATE_OPTIONS } from 'constants/buildingDate';
import { goToFindCompsResult } from 'components/FindComps/utils';
import { SALE_DATE_OPTIONS } from 'constants/saleDate';
import { FindCompTabs, FindCompTabsViews } from 'constants/findCompTabs';
import { StatusTypes, StatusTypesIDs } from 'constants/statusTypes';
import { LEASE_EXPIRING_TIME_OPTIONS } from 'constants/leaseTime';
import {
  I18N_AVANT_PROPERTY_COMMON_LABEL_PATH,
  I18N_AVANT_PROPERTY_ENUMERATORS,
  I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH,
  I18N_PLATFORM_COMMON_WORD_PATH,
} from 'constants/i18n';
import { LEASE_COMP_STATUSES } from 'constants/compStatuses';
import { PropertySubtypes } from 'constants/propertySubtypes';
import { IMarket, ISubmarket } from 'interfaces/IMarket';
import { IUser } from 'interfaces/IUser';
import { IDynamicURL } from 'interfaces/IDynamicURL';
import { IPropertySubType } from 'interfaces/IPropertySubType';
import { IPropertyClasses } from 'interfaces/IPropertyClasses';
import { IdName } from 'interfaces/IdName';
import locations from 'routes';
import { arrayToMap } from 'utils/objects';
import { translateText } from 'utils/i18n';

export interface IMenuLink {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface IMenuDropdownItem {
  title: string;
  links: IMenuLink[];
  classname?: string;
}

//@TODO - Remove this variable when we are getting the correct data from DB
export const marketAnalyticsNames: { [key: number]: string } = {
  0: '',
  1: t(`${I18N_PLATFORM_COMMON_WORD_PATH}.development`),
  2: t(`${I18N_PLATFORM_COMMON_WORD_PATH}.leasing`),
  3: t(`${I18N_PLATFORM_COMMON_WORD_PATH}.sale_plural`),
  5: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.largeBlocks`),
  6: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.historicalStats`),
  7: t(`${I18N_AVANT_PROPERTY_ENUMERATORS}.propertyTypes.dataCenter`),
  8: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.dataCenterLand`),
  9: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.flexOfficeSupply`),
  10: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.ventureCapitalFunding`),
  11: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.marketVitalityIndex`),
  12: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.fortune500`),
  25: t(`${I18N_AVANT_PROPERTY_PAGES_HOME_LABEL_PATH}.netLease`),
};

export const getMarketAnalyticsLinks = (
  history: History,
  dashboards: IDynamicURL[],
  ids: number[],
  fullAccess: boolean,
): IMenuDropdownItem[] => {
  if (!dashboards) return [];

  let dashboardsWithAccess: IDynamicURL[] = [];
  if (fullAccess) {
    dashboardsWithAccess = dashboards.filter(x => x.id !== 4);
  } else {
    const dashboardMaps: any = arrayToMap(ids || []);
    dashboardsWithAccess = dashboards.filter(
      x => x.id !== 4 && dashboardMaps[x.id!],
    );
  }

  if (dashboardsWithAccess.length === 0) return [];

  const onLinkRedirect = (dashboard: IDynamicURL) => {
    const parsed = new URL(dashboard.link!);
    const isPowerBi = parsed.hostname === 'app.powerbi.com';

    if (isPowerBi) {
      window.open(dashboard.link, '_blank');
      return;
    }

    return history.push(
      locations.marketAnalytics({
        dashboardId: dashboard.id,
      }),
    );
  };

  const toMenuLink = (dashboard: IDynamicURL): IMenuLink => ({
    label: dashboard.name || '',
    onClick: () => onLinkRedirect(dashboard),
  });

  const linksByGroup = dashboardsWithAccess.reduce<Record<string, IMenuLink[]>>(
    (acc, dashboard) => {
      const group = dashboard.group;
      if (!acc[group]) acc[group] = [];
      acc[group].push(toMenuLink(dashboard));
      return acc;
    },
    {},
  );

  return Object.entries(linksByGroup).map(([title, links]) => ({
    title,
    links,
  }));
};

interface IPropertyOptions {
  label: string;
  onClick: () => void;
}

export const getSubmarketList = (
  submarkets: ISubmarket[],
  history: History,
) => {
  const officeSubmarketLinks = submarkets
    ?.filter(s => s.shapeGroup === PropertySubtypes.office)
    .map(s => {
      return {
        onClick: () => history.push(locations.showSubmarket(s.id, 1)),
        label: s.name,
      };
    });

  const industrialSubmarketLinks = submarkets
    ?.filter(s => s.shapeGroup === PropertySubtypes.industrial)
    .map(s => {
      return {
        onClick: () => history.push(locations.showSubmarket(s.id, 2)),
        label: s.name,
      };
    });

  return [
    {
      title: translateText(
        `${I18N_AVANT_PROPERTY_ENUMERATORS}.propertyTypes.office`,
      ),
      links: officeSubmarketLinks,
    },
    {
      title: translateText(
        `${I18N_AVANT_PROPERTY_ENUMERATORS}.propertyTypes.industrial`,
      ),
      links: industrialSubmarketLinks,
    },
  ];
};

export const getCompPropertiesLinks = (
  user: IUser,
  userMarkets: IMarket[],
  userPreferredType: IdName | null,
  userPreferredSubTypes: IPropertySubType[],
  propertyClasses: IPropertyClasses[],
  history: History,
  onLinkClick: () => void,
): IMenuDropdownItem[] => {
  const propertyOptions: IPropertyOptions[] = [];

  propertyClasses.forEach(propClass => {
    const newPropertyOption = {
      label: propClass.name,
      onClick: () => {
        onLinkClick();
        goToFindCompsResult(
          user,
          history,
          userMarkets,
          FindCompTabs.properties,
          {
            types: [userPreferredType ?? {}],
            classes: [propClass],
          },
        );
      },
    };

    propertyOptions.push(newPropertyOption);
  });

  userPreferredSubTypes.forEach(subType => {
    if (!subType.name) return;
    const newPropertyOption = {
      label: subType.name,
      onClick: () => {
        onLinkClick();
        goToFindCompsResult(
          user,
          history,
          userMarkets,
          FindCompTabs.properties,
          {
            types: [userPreferredType ?? {}],
            subtypes: [subType],
          },
        );
      },
    };

    propertyOptions.push(newPropertyOption);
  });

  propertyOptions.push({
    label: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.all`),
    onClick: () => {
      onLinkClick();
      goToFindCompsResult(
        user,
        history,
        userMarkets,
        FindCompTabs.properties,
        {},
        {},
        FindCompTabsViews.list,
      );
    },
  });

  return [
    {
      title: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.lease_plural`),
      links: [
        {
          label: translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.leaseComp_plural`,
          ),
          onClick: () => {
            const compStatuses = LEASE_COMP_STATUSES.map(c => ({
              id: c.id,
            }));
            onLinkClick();

            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.leases,
              {},
              { confidential: false, compStatuses },
              FindCompTabsViews.list,
            );
          },
        },
        {
          label: translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.leaseExpirations`,
          ),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.leases,
              {},
              {
                expirationTime: LEASE_EXPIRING_TIME_OPTIONS[2],
                recordType: RECORD_TYPE_OPTIONS[1],
                confidential: false,
              },
              FindCompTabsViews.list,
            );
          },
        },
        {
          label: translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.tenantsInMarket`,
          ),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.tims,
              {},
              {},
              FindCompTabsViews.list,
            );
          },
        },
      ],
    },
    {
      title: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.property_plural`),
      links: propertyOptions,
    },
    {
      title: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.development`),
      links: [
        {
          label: translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.recentDeliveries`,
          ),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.properties,
              {
                builtYears: [
                  {
                    id: BUILDING_DATE_OPTIONS[0].id,
                    name: BUILDING_DATE_OPTIONS[0].name,
                  },
                ],
              },
              {},
              FindCompTabsViews.list,
            );
          },
        },
        {
          label: translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.underDevelopment`,
          ),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.properties,
              {
                statuses: [
                  {
                    id: StatusTypesIDs.UnderConstruction,
                    name: StatusTypes.UnderConstruction,
                  },
                  {
                    id: StatusTypesIDs.UnderRenovation,
                    name: StatusTypes.UnderRenovation,
                  },
                ],
              },
              {},
              FindCompTabsViews.list,
            );
          },
        },
        {
          label: translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.proposedProperties`,
          ),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.properties,
              {
                statuses: [
                  {
                    id: StatusTypesIDs.ProposedShortTerm,
                    name: StatusTypes.ProposedShortTerm,
                  },
                  {
                    id: StatusTypesIDs.ProposedLongTerm,
                    name: StatusTypes.ProposedLongTerm,
                  },
                ],
              },
              {},
              FindCompTabsViews.list,
            );
          },
        },
        {
          label: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.all`),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.properties,
              {},
              {},
              FindCompTabsViews.list,
            );
          },
        },
      ],
    },
    {
      title: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.sale_plural`),
      links: [
        {
          label: translateText(
            `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.recentSales`,
          ),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.sales,
              {},
              {},
              FindCompTabsViews.list,
              {
                date: {
                  id: SALE_DATE_OPTIONS[2].id,
                  name: SALE_DATE_OPTIONS[2].name,
                },
              },
            );
          },
        },
        {
          label: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.all`),
          onClick: () => {
            onLinkClick();
            goToFindCompsResult(
              user,
              history,
              userMarkets,
              FindCompTabs.sales,
              {},
              {},
              FindCompTabsViews.list,
            );
          },
        },
      ],
    },
    ...(window._env_['REACT_APP_DISPLAY_DOCUMENTS'] === 'true'
      ? [
          {
            title: translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.document_plural`,
            ),
            links: [
              {
                label: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.all`),
                onClick: () => {
                  onLinkClick();
                  goToFindCompsResult(
                    user,
                    history,
                    userMarkets,
                    FindCompTabs.documents,
                    {},
                    {},
                    FindCompTabsViews.list,
                  );
                },
              },
            ],
          },
        ]
      : []),
  ];
};

export const getTopFiveMarketList = (listMarket?: IMarket[]) => {
  const orderedByPropertyCount = listMarket?.sort((a, b) => {
    return (b?.propertiesCount || 0) - (a.propertiesCount || 0);
  });

  return orderedByPropertyCount && orderedByPropertyCount.length > 5
    ? orderedByPropertyCount.slice(0, 5)
    : orderedByPropertyCount;
};

export const getMappingLinks = (): IMenuDropdownItem[] => [
  {
    title: translateText(`${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.products`),
    links: [
      {
        label: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.panoramaTitle`),
        onClick: () => window.open(avantCitiesRoutes.panorama(), '_blank'),
      },
      {
        label: translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.officeTitle`),
        onClick: () => window.open(avantCitiesRoutes.office(), '_blank'),
      },
      {
        label: translateText(
          `${I18N_PLATFORM_COMMON_WORD_PATH}.healthcareTitle`,
        ),
        onClick: () => window.open(avantCitiesRoutes.healthcare(), '_blank'),
      },
      {
        label: translateText(
          `${I18N_AVANT_PROPERTY_COMMON_LABEL_PATH}.parcelFinder`,
        ),
        onClick: () => window.open(avantCitiesRoutes.parcelFinder(), '_blank'),
      },
    ],
  },
];
